import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Spin, Descriptions, Table, Tag, Typography, Card, Breadcrumb, Tabs,
    Progress, Grid, Row, Col
} from 'antd';
import { RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { souscripteurService } from '../../common/services';



const { Title } = Typography;
const { TabPane } = Tabs;

const DetailSouscripteur = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const controlSize = isMobile ? 'small' : 'middle';
    const cardSize = isMobile ? 'small' : 'default';
    const crumbFS = isMobile ? 14 : 16;
    const titleLevel = isMobile ? 4 : 3;

    const formatMoney = (v) =>
        (Number(v || 0)).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' FCFA';

    useEffect(() => {
        const run = async () => {
            try {
                setLoading(true);
                const d = await souscripteurService.getById(id);
                setData(d);
            } catch (e) {
                console.error('Erreur détail souscripteur:', e);
            } finally {
                setLoading(false);
            }
        };
        if (id) run();
    }, [id]);

    const percentPaid = useMemo(() => {
        if (!data) return 0;
        const paid = Number(data.paidTotal || 0);
        const total = Number(data.total || 0);
        if (!total) return 0;
        return Math.min(100, Math.round((paid / total) * 100));
    }, [data]);

    const planningColumns = [
        { title: 'Date', dataIndex: 'date', render: (d) => dayjs(d).format('DD/MM/YYYY') },
        { title: 'Montant', dataIndex: 'montant', align: 'right', render: formatMoney },
        { title: 'Payé', dataIndex: 'paidAmount', align: 'right', render: formatMoney },
        {
            title: 'Statut',
            dataIndex: 'status',
            render: (s) => {
                const color = s === 'paid' ? 'green' : s === 'overdue' ? 'red' : 'blue';
                const label = s === 'paid' ? 'Payée' : s === 'overdue' ? 'En retard' : 'À venir';
                return <Tag color={color}>{label}</Tag>;
            },
        },
        { title: 'Payé le', dataIndex: 'paidAt', render: (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—') },
    ];

    const paymentsColumns = [
        { title: 'Date', dataIndex: 'createdAt', render: (d) => dayjs(d).format('DD/MM/YYYY HH:mm') },
        { title: 'Provider', dataIndex: 'provider' },
        { title: 'Raison', dataIndex: 'reason', render: (r) => <Tag>{r}</Tag> },
        { title: 'Montant', dataIndex: 'amount', align: 'right', render: formatMoney },
        { title: 'Statut', dataIndex: 'status', render: (s) => <Tag color={s === 'succeeded' ? 'green' : s === 'pending' ? 'blue' : 'red'}>{s}</Tag> },
        { title: 'Ref', dataIndex: 'providerPaymentId' },
    ];

    if (loading || !data) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <Spin tip="Chargement des détails..." size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: isMobile ? 12 : 24 }}>
            <Breadcrumb
                separator={<RightOutlined style={{ fontSize: crumbFS, color: '#999', fontWeight: 'bolder' }} />}
                style={{ marginBottom: isMobile ? 12 : 16 }}
            >
                <Breadcrumb.Item>
                    <Link to="/dashboard" style={{ fontSize: crumbFS, fontWeight: 'bolder' }}>🏠 ACCUEIL</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Link to="/dashboard/souscripteurs" style={{ fontSize: crumbFS, fontWeight: 'bolder' }}>👥 SOUSCRIPTEURS</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <span style={{ fontSize: crumbFS, fontWeight: 'bolder' }}>🔍 DÉTAIL</span>
                </Breadcrumb.Item>
            </Breadcrumb>

            <Card bordered size={cardSize}>
                <Title level={titleLevel} style={{ marginBottom: isMobile ? 12 : 16 }}>
                    {data.user?.firstName} {data.user?.lastName}
                </Title>

                <Tabs defaultActiveKey="resume" size={controlSize} tabBarGutter={isMobile ? 8 : 16}>
                    {/* Résumé */}
                    <TabPane tab="📌 Résumé" key="resume">
                        <Row gutter={[12, 12]}>
                            <Col xs={24} md={14}>
                                <Descriptions bordered column={1} size={controlSize} title="Souscription">
                                    <Descriptions.Item label="Souscripteur">
                                        {data.user?.firstName} {data.user?.lastName} — {data.user?.email || '—'} — {data.user?.phone || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Site">
                                        {data.site?.nom} ({data.site?.localisation})
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Lot">
                                        {data.lot?.intitule} — {data.lot?.superficieM2} m² — {formatMoney(data.lot?.prixTotal)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Fréquence">{data.frequence}</Descriptions.Item>
                                    <Descriptions.Item label="Montant / échéance">{formatMoney(data.montantParEcheance)}</Descriptions.Item>
                                    <Descriptions.Item label="Total">{formatMoney(data.total)}</Descriptions.Item>
                                    <Descriptions.Item label="Payé">{formatMoney(data.paidTotal)}</Descriptions.Item>
                                    <Descriptions.Item label="Reste">
                                        <b>{formatMoney(data.remaining)}</b>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Statut">
                                        <Tag color={data.status === 'active' ? 'green' : data.status === 'terminee' ? 'default' : 'red'}>
                                            {data.status}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Prochaine échéance">
                                        {data.nextInstallment?.date
                                            ? `${dayjs(data.nextInstallment.date).format('DD/MM/YYYY')} — ${formatMoney(data.nextInstallment.montant)}`
                                            : '—'}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Col>

                            <Col xs={24} md={10}>
                                <Card size="small" title="Progression du paiement" bordered>
                                    <Progress percent={percentPaid} status={percentPaid === 100 ? 'success' : 'active'} />
                                    <div style={{ marginTop: 8, color: '#666' }}>
                                        {formatMoney(data.paidTotal)} / {formatMoney(data.total)}
                                    </div>
                                </Card>

                                <Card size="small" title="Échéances (stats)" bordered style={{ marginTop: 12 }}>
                                    <div>Total échéances&nbsp;: {data.installmentsStats?.totalCount ?? 0}</div>
                                    <div>Échéances payées&nbsp;: {data.installmentsStats?.paidCount ?? 0}</div>
                                </Card>
                            </Col>
                        </Row>
                    </TabPane>

                    {/* Planning */}
                    <TabPane tab="🗓️ Planning" key="planning">
                        <Table
                            size={controlSize}
                            rowKey={(r, idx) => idx}
                            columns={planningColumns}
                            dataSource={data.planning || []}
                            pagination={{ pageSize: 10, showSizeChanger: true }}
                            scroll={{ x: true, y: isMobile ? 360 : undefined }}
                            sticky
                        />
                    </TabPane>

                    {/* Paiements */}
                    <TabPane tab="💳 Paiements" key="payments">
                        <Table
                            size={controlSize}
                            rowKey="_id"
                            columns={paymentsColumns}
                            dataSource={data.payments || []}
                            pagination={{ pageSize: 10, showSizeChanger: true }}
                            scroll={{ x: true, y: isMobile ? 360 : undefined }}
                            sticky
                            footer={() => (
                                <div style={{ textAlign: isMobile ? 'center' : 'right' }}>
                                    <b>Total encaissé&nbsp;: {formatMoney(data.paidTotal)}</b>
                                </div>
                            )}
                        />
                    </TabPane>
                </Tabs>
            </Card>
        </div>
    );
};

export default DetailSouscripteur;
